import LecturerLayout from '../../components/LecturerLayout';
import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import AuthService from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

export default function Profile() {
    const stored = AuthService.getUser();
    const superAdmin = stored?.superAdmin;
    const staff = superAdmin?.staff || stored?.staff || null;

    const isSuperAdmin = !!superAdmin?.superAdminId || superAdmin?.staff?.type === 'admin';

    // Form states
    const [name, setName] = useState(staff?.name || '');
    const [username, setUsername] = useState(superAdmin?.username || '');
    const [officeEmail, setOfficeEmail] = useState(staff?.officeEmail || '');
    const [gmail, setGmail] = useState(staff?.gmail || '');
    const [mobileNumber, setMobileNumber] = useState(staff?.mobileNumber || '');
    const [gender, setGender] = useState(staff?.gender || '');
    const [title, setTitle] = useState(staff?.title || '');
    const [rosters, setRosters] = useState(staff?.rosters || []);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Password visibility states (align with admin Profile behavior)
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Image upload state
    const fileInputRef = useRef(null);

    // Avatar error state (for broken image URLs)
    const [avatarError, setAvatarError] = useState(false);

    // Loading states
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    // Load existing roster for logged-in staff on mount
    useEffect(() => {
        const loadRoster = async () => {
            try {
                const resp = await axiosInstance.get('/admin/lecturer/rosters');
                if (resp.data?.success && Array.isArray(resp.data.rosters)) {
                    setRosters(resp.data.rosters);
                }
            } catch (error) {
                console.error('Failed to load staff roster for profile:', error);
            }
        };

        loadRoster();
    }, []);

    // Handle image upload click
    const handleImageUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Handle file selection (align with admin Profile behavior)
    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File',
                text: 'Please select an image file (JPEG, PNG, etc.)',
            });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'Please select an image smaller than 5MB',
            });
            return;
        }

        if (!staff?.staffId) return;
        try {
            Swal.fire({
                title: 'Uploading...',
                text: 'Please wait while we upload the image',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const form = new FormData();
            form.append('image', file);

            // Use lecturer profile image endpoint (honors admin vs staff via api key)
            const resp = await axiosInstance.post('/admin/lecturer/profile/image', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (!resp.data?.success) {
                throw new Error(resp.data?.message || 'Failed to upload image');
            }

            const newPath = resp.data.filename || resp.data.imagePath;
            if (newPath) {
                window.dispatchEvent(
                    new CustomEvent('ka-user-avatar-updated', {
                        detail: { staffId: staff.staffId, filename: newPath },
                    })
                );
            }

            Swal.fire({ icon: 'success', title: 'Success!', text: resp.data?.message || 'Profile image uploaded successfully', timer: 1800 });
            // Reload to pick up updated imagePath from backend
            setTimeout(() => window.location.reload(), 600);
        } catch (error) {
            console.error('Error uploading image:', error);
            Swal.fire({ icon: 'error', title: 'Upload Failed', text: error.message || 'Failed to upload image. Please try again.' });
        } finally {
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Handle profile update (align with admin Profile behavior)
    const handleProfileUpdate = async e => {
        e.preventDefault();

        if (!name.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Name is required',
            });
            return;
        }

        // Validate work schedule
        const offDays = rosters.filter(r => r.dayType === 'off day').length;
        const morningHalfDays = rosters.filter(r => r.dayType === 'morning half day').length;
        const eveningHalfDays = rosters.filter(r => r.dayType === 'evening half day').length;

        if (rosters.length > 0) {
            if (offDays !== 1) {
                Swal.fire({
                    icon: 'error',
                    title: 'Work Schedule Error',
                    text: 'You must have exactly one off day in your work schedule',
                });
                return;
            }

            if ((morningHalfDays === 0 && eveningHalfDays === 0) || (morningHalfDays > 1 || eveningHalfDays > 1)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Work Schedule Error',
                    text: 'You must have exactly one morning half day OR one evening half day in your work schedule',
                });
                return;
            }

            if (morningHalfDays > 0 && eveningHalfDays > 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Work Schedule Error',
                    text: 'You cannot have both morning half day and evening half day. Choose only one.',
                });
                return;
            }
        }

        setIsUpdatingProfile(true);

        const formData = {
            name,
            // For admins, username is required and updates both super_admins and staff
            username: isSuperAdmin ? username : undefined,
            officeEmail,
            gmail,
            mobileNumber,
            gender,
            title,
            // Backend expects an array; it validates constraints and upserts staff_rosters
            rosters,
        };

        try {
            const resp = await axiosInstance.put('/admin/lecturer/profile', formData);
            if (resp.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: resp.data?.message || 'Profile updated successfully',
                    timer: 2000,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: resp.data?.message || 'Failed to update profile',
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to update profile';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: msg,
            });
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    // Handle password update (align with admin Profile behavior)
    const handlePasswordUpdate = async e => {
        e.preventDefault();

        if (!currentPassword || !newPassword || !confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'All password fields are required',
            });
            return;
        }

        if (newPassword.length < 6) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'New password must be at least 6 characters long',
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'New password and confirm password do not match',
            });
            return;
        }

        setIsUpdatingPassword(true);

        try {
            const resp = await axiosInstance.put('/admin/lecturer/password', {
                current_password: currentPassword,
                new_password: newPassword,
                new_password_confirmation: confirmPassword,
            });

            if (resp.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: resp.data?.message || 'Password updated successfully',
                    timer: 2000,
                });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: resp.data?.message || 'Failed to update password',
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to update password';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: msg,
            });
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    return (
        <LecturerLayout title="Profile">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header with gradient */}
                <div className="mb-8 bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl shadow-lg p-8 text-white">
                    <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                            {staff?.imagePath && !avatarError ? (
                                <img
                                    src={getImageURL(staff.imagePath)}
                                    alt={name}
                                    onError={() => setAvatarError(true)}
                                    className="h-20 w-20 rounded-full object-cover border-4 border-white/20 shadow-lg"
                                />
                            ) : (
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10 text-2xl font-bold text-white border-4 border-white/20 shadow-lg">
                                    {name
                                        ? name.split(' ')
                                            .map((n) => n[0])
                                            .join('')
                                            .toUpperCase()
                                        : 'U'}
                                </div>
                            )}
                        </div>
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold">{name || 'User Profile'}</h1>
                            <p className="mt-1 text-slate-200 flex items-center gap-2">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {staff?.employeeNumber && `Employee #${staff.employeeNumber} • `}
                                {isSuperAdmin ? 'Administrator' : 'Staff Member'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* Profile Image Card */}
                    <div className="lg:col-span-1">
                        <div className="overflow-hidden rounded-xl bg-white shadow-lg border border-gray-200">
                            <div className="bg-slate-50 px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    </svg>
                                    Profile Picture
                                </h2>
                            </div>
                            <div className="p-6">
                                {/* Avatar Display */}
                                <div className="flex flex-col items-center">
                                    {staff?.imagePath && !avatarError ? (
                                        <div className="relative group">
                                            <img
                                                src={getImageURL(staff.imagePath)}
                                                alt={name}
                                                onError={() => setAvatarError(true)}
                                                className="h-40 w-40 rounded-full object-cover border-4 border-slate-200 shadow-xl transition-all group-hover:border-slate-300"
                                            />
                                            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                </svg>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-700 text-5xl font-bold text-white border-4 border-slate-200 shadow-xl">
                                            {name
                                                ? name.split(' ')
                                                    .map((n) => n[0])
                                                    .join('')
                                                    .toUpperCase()
                                                : 'U'}
                                        </div>
                                    )}
                                    
                                    <button
                                        onClick={handleImageUploadClick}
                                        className="mt-6 w-full rounded-lg bg-slate-700 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                                    >
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Change Photo
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Profile Information Card */}
                    <div className="lg:col-span-2">
                        <div className="overflow-hidden rounded-xl bg-white shadow-lg border border-gray-200">
                            <div className="bg-slate-50 px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Personal Information
                                </h2>
                            </div>
                            <div className="p-8">
                                <form onSubmit={handleProfileUpdate}>
                                    <div className="space-y-6">
                                        {/* Name */}
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                                placeholder="Enter your full name"
                                                required
                                            />
                                        </div>

                                        {/* Username (Super Admin only) */}
                                        {isSuperAdmin && (
                                            <div>
                                                <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                    <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                    </svg>
                                                    Username <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="username"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                                    placeholder="Enter your username"
                                                    required
                                                />
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Office Email */}
                                            <div>
                                                <label htmlFor="officeEmail" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                    <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    Office Email
                                                </label>
                                                <input
                                                    type="email"
                                                    id="officeEmail"
                                                    value={officeEmail}
                                                    onChange={(e) => setOfficeEmail(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                                    placeholder="office@example.com"
                                                />
                                            </div>

                                            {/* Gmail */}
                                            <div>
                                                <label htmlFor="gmail" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                    <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    Personal Email
                                                </label>
                                                <input
                                                    type="email"
                                                    id="gmail"
                                                    value={gmail}
                                                    onChange={(e) => setGmail(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                                    placeholder="personal@gmail.com"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Mobile Number */}
                                            <div>
                                                <label htmlFor="mobileNumber" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                    <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                    </svg>
                                                    Mobile Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="mobileNumber"
                                                    value={mobileNumber}
                                                    onChange={(e) => setMobileNumber(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                                    placeholder="+94 77 123 4567"
                                                />
                                            </div>

                                            {/* Gender */}
                                            <div>
                                                <label htmlFor="gender" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                    <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                                    </svg>
                                                    Gender
                                                </label>
                                                <select
                                                    id="gender"
                                                    value={gender}
                                                    onChange={(e) => setGender(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                                >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Title */}
                                        <div>
                                            <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                                Title
                                            </label>
                                            <select
                                                id="title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 transition-colors"
                                            >
                                                <option value="">Select Title</option>
                                                <option value="Mr">Mr</option>
                                                <option value="Mrs">Mrs</option>
                                                <option value="Miss">Miss</option>
                                                <option value="Ms">Ms</option>
                                                <option value="Dr">Dr</option>
                                            </select>
                                        </div>

                                        {/* Work Schedule/Roster */}
                                        <div className="pt-6 mt-6 border-t-2 border-gray-200">
                                            <div className="flex items-center gap-2 mb-4">
                                                <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <h3 className="text-base font-semibold text-gray-900">Weekly Schedule</h3>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-4 space-y-2.5">
                                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                                                    const roster = rosters.find(r => r.rosterDay === day);
                                                    const dayType = roster?.dayType || '';
                                                    
                                                    // Get color based on day type
                                                    let bgColor = 'bg-white';
                                                    let borderColor = 'border-gray-200';
                                                    let badgeColor = 'bg-gray-100 text-gray-600';
                                                    
                                                    if (dayType === 'off day') {
                                                        bgColor = 'bg-red-50';
                                                        borderColor = 'border-red-200';
                                                        badgeColor = 'bg-red-100 text-red-700';
                                                    } else if (dayType === 'morning half day') {
                                                        bgColor = 'bg-blue-50';
                                                        borderColor = 'border-blue-200';
                                                        badgeColor = 'bg-blue-100 text-blue-700';
                                                    } else if (dayType === 'evening half day') {
                                                        bgColor = 'bg-purple-50';
                                                        borderColor = 'border-purple-200';
                                                        badgeColor = 'bg-purple-100 text-purple-700';
                                                    }
                                                    
                                                    return (
                                                        <div key={day} className={`flex items-center gap-4 p-3 rounded-lg border-2 transition-all ${bgColor} ${borderColor}`}>
                                                            <div className="flex items-center gap-2 w-32">
                                                                <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                                                                <span className="text-sm font-semibold text-gray-700">{day}</span>
                                                            </div>
                                                            <div className="flex-1">
                                                                <select
                                                                    value={dayType}
                                                                    onChange={(e) => {
                                                                        const selectedType = e.target.value;
                                                                        let newRosters = [...rosters];
                                                                        
                                                                        // If selecting off day, morning half day, or evening half day
                                                                        // Remove any other day with the same type
                                                                        if (selectedType === 'off day') {
                                                                            // Remove all other off days
                                                                            newRosters = newRosters.filter(r => 
                                                                                r.rosterDay === day || r.dayType !== 'off day'
                                                                            );
                                                                        } else if (selectedType === 'morning half day') {
                                                                            // Remove all other morning half days AND all evening half days
                                                                            newRosters = newRosters.filter(r => 
                                                                                r.rosterDay === day || 
                                                                                (r.dayType !== 'morning half day' && r.dayType !== 'evening half day')
                                                                            );
                                                                        } else if (selectedType === 'evening half day') {
                                                                            // Remove all other evening half days AND all morning half days
                                                                            newRosters = newRosters.filter(r => 
                                                                                r.rosterDay === day || 
                                                                                (r.dayType !== 'evening half day' && r.dayType !== 'morning half day')
                                                                            );
                                                                        }
                                                                        
                                                                        // Remove current day from rosters
                                                                        newRosters = newRosters.filter(r => r.rosterDay !== day);
                                                                        
                                                                        // Add new roster entry if not "Not Set"
                                                                        if (selectedType) {
                                                                            newRosters.push({
                                                                                rosterId: roster?.rosterId || 0,
                                                                                rosterDay: day,
                                                                                dayType: selectedType
                                                                            });
                                                                        }
                                                                        
                                                                        setRosters(newRosters);
                                                                    }}
                                                                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 font-medium transition-colors"
                                                                >
                                                                    <option value="">🟢 Full Day</option>
                                                                    <option value="morning half day">🌅 Morning Half Day</option>
                                                                    <option value="evening half day">🌆 Evening Half Day</option>
                                                                    <option value="off day">🔴 Off Day</option>
                                                                </select>
                                                            </div>
                                                            {dayType && (
                                                                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${badgeColor}`}>
                                                                    {dayType === 'off day' ? '🔴 Off' : 
                                                                     dayType === 'morning half day' ? '🌅 AM' : 
                                                                     dayType === 'evening half day' ? '🌆 PM' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="mt-3 flex items-start gap-2 text-xs text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                                                <svg className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p><strong>Note:</strong> You must select exactly one off day and one half day (morning OR evening) per week.</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="mt-8 flex gap-3">
                                        <button
                                            type="submit"
                                            disabled={isUpdatingProfile}
                                            className="flex-1 rounded-lg bg-slate-700 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                                        >
                                            {isUpdatingProfile ? (
                                                <>
                                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Update Profile
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Change Password Card */}
                        <div className="mt-6 overflow-hidden rounded-xl bg-white shadow-lg border border-gray-200">
                            <div className="bg-slate-50 px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Security Settings
                                </h2>
                            </div>
                            <div className="p-8">
                                <form onSubmit={handlePasswordUpdate}>
                                    <div className="space-y-6">
                                        {/* Current Password */}
                                        <div>
                                            <label htmlFor="currentPassword" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                </svg>
                                                Current Password <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    id="currentPassword"
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 pr-10 transition-colors"
                                                    placeholder="Enter current password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                                    aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d={showCurrentPassword
                                                                ? 'M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.046.38-2.083 1.035-3.054M6.228 6.228A9.957 9.957 0 0112 5c5 0 9 4 9 7 0 1.273-.45 2.492-1.257 3.57M9.88 9.88a3 3 0 104.24 4.24'
                                                                : 'M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7zm9.542-3a3 3 0 100 6 3 3 0 000-6z'}
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* New Password */}
                                        <div>
                                            <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                New Password <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    id="newPassword"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 pr-10 transition-colors"
                                                    placeholder="Enter new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword((prev) => !prev)}
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d={showNewPassword
                                                                ? 'M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.046.38-2.083 1.035-3.054M6.228 6.228A9.957 9.957 0 0112 5c5 0 9 4 9 7 0 1.273-.45 2.492-1.257 3.57M9.88 9.88a3 3 0 104.24 4.24'
                                                                : 'M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7zm9.542-3a3 3 0 100 6 3 3 0 000-6z'}
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                            <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Minimum 6 characters required
                                            </p>
                                        </div>

                                        {/* Confirm Password */}
                                        <div>
                                            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Confirm New Password <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    id="confirmPassword"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 text-base px-4 py-3 pr-10 transition-colors"
                                                    placeholder="Re-enter new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d={showConfirmPassword
                                                                ? 'M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.046.38-2.083 1.035-3.054M6.228 6.228A9.957 9.957 0 0112 5c5 0 9 4 9 7 0 1.273-.45 2.492-1.257 3.57M9.88 9.88a3 3 0 104.24 4.24'
                                                                : 'M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7zm9.542-3a3 3 0 100 6 3 3 0 000-6z'}
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="mt-8">
                                        <button
                                            type="submit"
                                            disabled={isUpdatingPassword}
                                            className="w-full rounded-lg bg-slate-700 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                                        >
                                            {isUpdatingPassword ? (
                                                <>
                                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Updating Password...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                    </svg>
                                                    Change Password
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </LecturerLayout>
    );
}
